'use client';

import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { AUTH_SERVICE_UNAVAILABLE_MESSAGE, isSupabaseTransportError } from '@/lib/auth/public-auth';
import { createBrowserClient } from '@/lib/supabase/client';

/**
 * HashFragmentHandler
 * 
 * Handles auth tokens passed via URL hash fragments (implicit flow).
 * This is needed because Supabase email confirmation links may redirect
 * with tokens in the hash fragment (#access_token=...) which are only
 * accessible client-side.
 * 
 * This component should be mounted on pages that might receive these redirects
 * (typically the root page or auth callback page).
 */
export function HashFragmentHandler() {
  const router = useRouter();
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    async function handleHashFragment() {
      // Only run in browser
      if (typeof window === 'undefined') return;

      const hash = window.location.hash;
      
      // Check if we have auth tokens in the hash
      if (!hash || !hash.includes('access_token')) {
        return;
      }

      setIsProcessing(true);

      try {
        const supabase = createBrowserClient();
        
        // Parse the hash fragment
        const hashParams = new URLSearchParams(hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const type = hashParams.get('type');

        if (accessToken && refreshToken) {
          // Set the session from the tokens
          const { error } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });

          if (error) {
            // Redirect to error page
            const message = isSupabaseTransportError(error)
              ? AUTH_SERVICE_UNAVAILABLE_MESSAGE
              : error.message;
            router.push(`/auth/error?error=${encodeURIComponent(message)}`);
            return;
          }

          // Clear the hash from the URL for security
          window.history.replaceState(null, '', window.location.pathname);

          // Redirect based on the type of auth event
          if (type === 'signup' || type === 'email') {
            // New signup or email confirmation - go to onboarding
            router.push('/onboarding');
          } else if (type === 'recovery') {
            // Password recovery - go to update password page
            router.push('/auth/update-password');
          } else {
            // Default redirect for authenticated users
            router.push('/protected');
          }
        }
      } catch (err) {
        const message = isSupabaseTransportError(err)
          ? AUTH_SERVICE_UNAVAILABLE_MESSAGE
          : err instanceof Error
            ? err.message
            : 'Authentication failed';
        router.push(`/auth/error?error=${encodeURIComponent(message)}`);
      }
    }

    handleHashFragment();
  }, [router]);

  // Show a loading overlay while processing auth tokens
  if (isProcessing) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
        <div className="text-center">
          <div className="mb-4">
            <svg
              className="animate-spin mx-auto h-8 w-8 text-primary"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
            >
              <circle
                className="opacity-25"
                cx="12"
                cy="12"
                r="10"
                stroke="currentColor"
                strokeWidth="4"
              />
              <path
                className="opacity-75"
                fill="currentColor"
                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
              />
            </svg>
          </div>
          <p className="text-muted-foreground">Completing authentication...</p>
        </div>
      </div>
    );
  }

  return null;
}


